import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { naicsQueryKey } from '@/hooks/federal-contract/types'
import { fetchNaicsCodes } from '@/hooks/federal-contract/useNaicsCodes'
import { useCodeSearch } from '@/hooks/search/useCodeSearch'

import type { PaginatedResponse } from '@/types/Api'
import type { NAICS } from '@/types/federal-contract/Api'

const fetchNAICSSearch = async (
  query: string,
  accessToken: string,
): Promise<PaginatedResponse<NAICS>> => {
  if (query.length < 1) {
    return { count: 0, has_more: false, data: [] }
  }
  const url = `${API_URL}/api/v1/naics/search?query=${query}&limit=50`
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: { status: res.status },
    })
  }
  return (await res.json()) as PaginatedResponse<NAICS>
}

// Module-level stable references for useMemo deps in useCodeSearch
const naicsSearchQueryKeyFn = (query: string) => naicsQueryKey.search({ query })
const naicsItemQueryKeyFn = (code: string) => naicsQueryKey.code({ code })
const naicsItemIdFn = (item: NAICS) => item.code
const naicsFetchItem = (code: string, accessToken: string) =>
  fetchNaicsCodes([code], accessToken).then((r) => r.data[0])

export const useNAICSCodeSearch = ({
  initialCodes,
}: {
  initialCodes: string[]
}) =>
  useCodeSearch<NAICS>({
    initialCodes,
    searchQueryKeyFn: naicsSearchQueryKeyFn,
    itemQueryKeyFn: naicsItemQueryKeyFn,
    fetchSearch: fetchNAICSSearch,
    fetchItem: naicsFetchItem,
    itemIdFn: naicsItemIdFn,
  })
